import React, {useCallback, useMemo} from 'react'
import styled from 'styled-components'
import {useTheme} from 'themes'
import {ObjectMapRef} from 'types/commons'
import {ListHandlerStatus} from 'types/components'
import {
  ButtonOutline,
  ButtonChoice,
  IndicatorBar,
  Modal,
  Paragraph,
} from 'common/components'
import {DropboxUploadMapData} from 'types/modules'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 335px;
  padding: 20px;
  align-items: center;
  gap: 20px;
`

const StyledSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledButton = styled(ButtonOutline)`
  width: 100%;
`

export default function DropboxUploadModal({
  visible,
  mapRef,
  onClose,
  onRetry,
  onViewReport,
}: {
  visible: boolean
  mapRef: ObjectMapRef<DropboxUploadMapData>
  onClose(): void
  onRetry(): void
  onViewReport(): void
}) {
  const {
    isRunning,
    total,
    totalSuccess,
    totalFailed,
    totalWarning,
    statuses,
  } = mapRef.map.current
  const theme = useTheme()

  const colorExtractor = useCallback(
    (status: ListHandlerStatus) => {
      switch (status) {
        case 'success':
          return theme.primary_5
        case 'failed':
          return theme.danger_5
        case 'warning':
          return theme.warning_5
        default:
          return 'transparent'
      }
    },
    [theme],
  )

  const uploadStatus = useMemo(() => {
    if (totalSuccess === total) return 'success'
    if (totalFailed === total) return 'failed'
    if (totalWarning === total) return 'duplicate'
    return 'mixed'
  }, [total, totalFailed, totalSuccess, totalWarning])

  const modalInfo = useMemo(() => {
    if (!uploadStatus) return null

    const map = {
      success: {
        title: "It's Done!",
        description: `${totalSuccess} contents uploaded successfully!`,
      },
      failed: {
        title: 'Failed to Upload',
        description: `${totalFailed} contents failed to upload.`,
      },
      duplicate: {
        title: 'Uploaded! But...',
        description: `${totalWarning} contents detected as duplicates.`,
      },
      mixed: {
        title: 'Uploaded! But...',
        description: 'View report for more detail.',
      },
    }

    return map[uploadStatus]
  }, [uploadStatus, totalSuccess, totalFailed, totalWarning])

  const renderContent = useMemo(
    () =>
      isRunning ? (
        <>
          <StyledSection>
            <Paragraph fontSize="xl" fontWeight="bold">
              Uploading...
            </Paragraph>
          </StyledSection>
          <IndicatorBar statuses={statuses} colorExtractor={colorExtractor} />
          <StyledSection>
            <Paragraph fontSize="l" color="gray_5">
              {`${totalSuccess} of ${total} Contents Uploaded.`}
            </Paragraph>
            <Paragraph fontSize="m" fontWeight="regular" color="gray_3">
              Please wait a moment, and don’t close this page.
            </Paragraph>
          </StyledSection>
        </>
      ) : (
        <>
          <Paragraph fontSize="xl" fontWeight="bold">
            {modalInfo?.title}
          </Paragraph>
          <IndicatorBar statuses={statuses} colorExtractor={colorExtractor} />
          <StyledSection>
            <Paragraph fontSize="l" color="gray_5">
              {modalInfo?.description}
            </Paragraph>
          </StyledSection>
          {totalFailed > 0 ? (
            <ButtonChoice
              buttonIsVertical
              secondaryIsOutline
              primaryLabel="Re-upload Failed Content"
              secondaryLabel="View Report"
              primaryOnClick={onRetry}
              secondaryOnClick={onViewReport}
            />
          ) : (
            <StyledButton
              borderColor="primary_5"
              borderHoverColor="primary_4"
              color="primary_5"
              label={uploadStatus === 'success' ? 'Close' : 'View Report'}
              onClick={uploadStatus === 'success' ? onClose : onViewReport}
            />
          )}
        </>
      ),
    [
      colorExtractor,
      isRunning,
      modalInfo?.description,
      modalInfo?.title,
      onClose,
      onRetry,
      onViewReport,
      statuses,
      total,
      totalFailed,
      totalSuccess,
      uploadStatus,
    ],
  )

  return (
    <Modal visible={visible}>
      <StyledContainer>{renderContent}</StyledContainer>
    </Modal>
  )
}
